import type { FC } from 'react';

import { useCms, useContent } from '@eneco/cms';
import RichTextComponent from '@eneco/components/RichText/RichText';
import { Accordion, Heading, Stack } from '@eneco/sparky';

import type { FaqAccordionContent, FaqItem } from './faq-accordion-content.types';

/**
 * `faqAccordion` block for the CMS-agnostic lane. POC-schema-only (see
 * `faq-accordion-content.types.ts`). Each item's `answer` goes through `adapter.toRichText` — same
 * Portable-Text-or-HTML-string handling as the `RichText` block — rather than being rendered as raw
 * Portable Text here.
 */
const FaqAccordion: FC = () => {
  const { adapter } = useCms();
  const content = useContent<FaqAccordionContent>();
  const items = (content.items ?? []).filter(item => item.question);

  if (items.length === 0) return null;

  return (
    <Stack gap="6">
      {content.title && <Heading as="h2">{content.title}</Heading>}
      <Accordion>
        {items.map(item => (
          <FaqAccordionItem key={item._key} item={item} toRichText={adapter.toRichText} />
        ))}
      </Accordion>
    </Stack>
  );
};

const FaqAccordionItem: FC<{ item: FaqItem; toRichText: (raw: unknown) => string | undefined }> = ({ item, toRichText }) => {
  const html = toRichText(item.answer);
  if (!html) return null;

  return (
    <Accordion.Item heading={item.question ?? ''} id={item._key}>
      <RichTextComponent html={html} />
    </Accordion.Item>
  );
};

export default FaqAccordion;
